import { useState } from "react";
import { Smartphone, RefreshCw, Loader2, Check, X, Mail } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function Line({ ok, label, detail, testid }) {
  return (
    <div className="flex items-start gap-2 text-sm" data-testid={testid}>
      {ok ? <Check className="h-4 w-4 text-emerald-600 mt-0.5" /> : <X className="h-4 w-4 text-slate-400 mt-0.5" />}
      <div><span className={ok ? "text-slate-900" : "text-slate-500"}>{label}</span>{detail && <div className="text-[11px] text-slate-400">{detail}</div>}</div>
    </div>
  );
}

export function InviteResultDialog({ result, onClose, onResend }) {
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  if (!result) return null;
  const resend = async (extra) => {
    setBusy(true);
    try { await onResend(extra); toast.success("Invite sent again"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="invite-result-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Smartphone className="h-5 w-5 text-primary" /> Parent app invite · {result.contact}</DialogTitle>
          <DialogDescription>
            {result.already_active ? "This parent already has an account — the link lets them re-link this child or reset their password." : "The parent opens the emailed link, sets a password and signs in with their mobile number."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <Line ok={result.sms_sent} label={result.sms_sent ? "Text message sent" : "Text message not sent"} detail={result.sms_error} testid="invite-sms-status" />
          <Line ok={result.email_sent} label={result.email_sent ? `Email sent to ${result.email_to}` : result.email_to ? `Email to ${result.email_to} failed` : "No email on file for this contact"} detail={result.email_error} testid="invite-email-status" />
        </div>
        <div className="rounded-xl border border-dashed border-slate-300 p-3 space-y-2">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Didn't receive it?</div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => resend({})} disabled={busy} data-testid="invite-resend-btn"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-100 transition disabled:opacity-60">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Resend invite
            </button>
            <div className="flex items-center gap-1.5 flex-1 min-w-[220px]">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Send to a different email…" data-testid="invite-email-input"
                className="flex-1 h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs outline-none focus:border-primary" />
              <button onClick={() => email && resend({ email })} disabled={busy || !email} data-testid="invite-email-send-btn"
                className="h-9 rounded-lg bg-slate-900 px-3 text-xs font-semibold text-white inline-flex items-center gap-1.5 disabled:opacity-50">
                <Mail className="h-3.5 w-3.5" /> Email
              </button>
            </div>
          </div>
          <div className="text-[11px] text-slate-400">Resending emails a fresh link; older links keep working until they expire (30 days).</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
